import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { SourceBadge, TranslationBadge } from '@/components/provenance-badge';
import { StateMarks } from './StateMarks';
import {
  formatAge,
  isOverdue,
  liveHours,
  type QueueSummary,
  type ReviewRowData,
} from './reviewTypes';

/**
 * One language of one khutbah.
 *
 * Six things could appear per row — two state marks, two provenance badges,
 * an age and a set of actions — which is a wall of pills if they are simply
 * laid side by side. The marks stack in one column and the badges in the
 * next, exactly as the kit specifies, so the row reads as four groups
 * rather than six chips.
 */
export function ReviewRow({
  row,
  summary,
  selected,
  busy,
  onOpen,
  onApprove,
  onReject,
  onFocus,
}: {
  row: ReviewRowData;
  summary: QueueSummary;
  selected: boolean;
  busy: boolean;
  onOpen: () => void;
  onApprove: () => void;
  onReject: () => void;
  onFocus: () => void;
}) {
  const hours = liveHours(row);
  const overdue = isOverdue(row, summary);

  return (
    <li
      onMouseDown={onFocus}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'relative grid gap-3 border-b border-border p-4',
        'md:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto] md:items-center md:gap-5',
        selected && 'bg-accent',
      )}
    >
      {/* The keyboard cursor, as an inline-start bar so it follows the
          writing direction rather than sitting on the left in Arabic. */}
      {selected ? (
        <span aria-hidden className="absolute inset-y-0 start-0 w-[3px] bg-primary" />
      ) : null}

      <div className="min-w-0">
        {row.titleAr ? (
          <p lang="ar" dir="rtl" className="script-ar truncate text-[15px] font-normal">
            {row.titleAr}
          </p>
        ) : (
          <p className="truncate text-sm italic text-muted-foreground">
            Untitled khutbah
          </p>
        )}
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {row.friday}
          {row.institutionNameAr ? (
            <>
              {' · '}
              <span lang="ar" dir="rtl" className="script-ar">
                {row.institutionNameAr}
              </span>
            </>
          ) : null}
          {' · '}
          {row.passages} passage{row.passages === 1 ? '' : 's'}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <span className="rounded-sm bg-muted px-2 py-1 text-[12px] font-semibold uppercase">
          {row.language}
        </span>
        <span className="text-xs text-muted-foreground">{row.languageName}</span>
      </div>

      <StateMarks row={row} checkEverRuns={summary.faithfulnessCheckOn} />

      {/* Both provenance badges, never merged: where the Arabic came from,
          and who produced this translation. */}
      <div className="flex flex-col items-start gap-1">
        <SourceBadge institutionKey={row.sourceKind === 'user' ? '' : row.institutionKey} />
        <TranslationBadge
          translation={{
            text: '',
            origin: row.origin,
            entryMethod: 'manual',
            status: 'published',
            reviewed: row.reviewed,
            checked: row.checked,
            modelId: null,
            declaredModel: row.declaredModel,
          }}
          stale={row.stale}
        />
      </div>

      <div className="flex flex-col items-start gap-2 md:items-end">
        <span
          className={cn(
            'text-xs tabular-nums',
            overdue ? 'font-semibold text-warning-foreground' : 'text-muted-foreground',
          )}
        >
          {hours === null
            ? // Not published, so it has no live age. Zero would read as
              // urgent when it is the opposite.
              'not published'
            : `${formatAge(hours)} live${overdue ? ' · overdue' : ''}`}
        </span>

        <div className="flex gap-1.5">
          <Button type="button" variant="ghost" size="sm" onClick={onOpen}>
            Open
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || row.reviewed || row.stale}
            onClick={onApprove}
            title={
              row.stale
                ? 'The Arabic was replaced — this cannot be approved until it is retranslated'
                : row.reviewed
                  ? 'Already approved'
                  : undefined
            }
          >
            Approve
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={busy || row.rejected}
            onClick={onReject}
            title={row.rejected ? 'Already rejected' : undefined}
          >
            Reject
          </Button>
        </div>
      </div>

      {row.rejected && row.rejectionNote ? (
        <div className="md:col-span-5">
          <p className="rounded-md bg-muted p-2.5 text-[13px] leading-[18px]">
            <span className="font-semibold">
              Rejected{row.withdrawn ? ' and withdrawn' : ', left readable'}
              {row.rejectionCauses.length > 0
                ? ` · ${row.rejectionCauses.join(', ')}`
                : ''}
              {' — '}
            </span>
            {row.rejectionNote}
          </p>
        </div>
      ) : null}
    </li>
  );
}
